<template>
    <b-container fluid class="eval-prompt-management py-4">
        <div class="page-header mb-4">
            <div>
                <div class="page-kicker mb-1">AI JUDGMENT CRITERIA</div>
                <h4 class="mb-2">AI 판단 기준</h4>
                <p class="text-muted mb-0">
                    회사별 AI 평가 피드백에서 공통 판단 기준을 찾고, 새 기준이 기존 기준보다 나쁘지 않은지 자동으로 시험합니다.
                </p>
            </div>
            <div class="header-status mt-3 mt-md-0">
                <b-badge variant="light" class="stage-badge mr-2">{{ stage.label }}</b-badge>
                <b-badge :variant="automationStatus.variant" class="stage-badge">
                    {{ automationStatus.label }}
                </b-badge>
            </div>
        </div>

        <b-card no-body class="rulebook-shell">
            <b-tabs v-model="tabIndex" card lazy>
                <b-tab :title="tabs[0].label">
                    <RulebookOverviewTab :overview="overview"
                                         :loading="loading"
                                         :error="error"
                                         @retry="loadOverview"
                                         @refresh-overview="loadOverview"
                    />
                </b-tab>
                <b-tab :title="tabs[1].label">
                    <RulebookFeedbackTab :overview="overview"
                                         :loading="loading"
                                         :error="error"
                                         @retry="loadOverview"
                    />
                </b-tab>
                <b-tab :title="tabs[2].label">
                    <RulebookVersionsTab @refresh-overview="loadOverview" />
                </b-tab>
                <b-tab :title="tabs[3].label">
                    <RulebookCyclesTab />
                </b-tab>
                <b-tab :title="tabs[4].label">
                    <RulebookValidationTab :overview="overview"
                                           :loading="loading"
                                           :error="error"
                                           @retry="loadOverview"
                    />
                </b-tab>
            </b-tabs>
        </b-card>
    </b-container>
</template>

<script>
import RulebookCyclesTab from './RulebookCyclesTab.vue';
import RulebookFeedbackTab from './RulebookFeedbackTab.vue';
import RulebookOverviewTab from './RulebookOverviewTab.vue';
import RulebookValidationTab from './RulebookValidationTab.vue';
import RulebookVersionsTab from './RulebookVersionsTab.vue';
import {
    RULEBOOK_TABS,
    apiErrorMessage,
    automationStatusMeta,
    normalizeRulebookOverview,
    stageMeta,
} from './rulebook-presentation';

export default {
    name: 'EvalPromptManagement',
    components: {
        RulebookCyclesTab,
        RulebookFeedbackTab,
        RulebookOverviewTab,
        RulebookValidationTab,
        RulebookVersionsTab,
    },
    data() {
        return {
            tabIndex: 0,
            tabs: RULEBOOK_TABS,
            overview: normalizeRulebookOverview({}),
            loading: false,
            error: '',
            overviewRequestId: 0,
        };
    },
    computed: {
        stage() {
            return stageMeta(this.overview.rulebook.stage);
        },
        automationStatus() {
            return automationStatusMeta(this.overview);
        },
    },
    created() {
        this.loadOverview();
    },
    methods: {
        async loadOverview() {
            // 스위치·복귀 직후 재조회가 먼저 시작된 조회에 덮이지 않도록 마지막 요청만 반영한다.
            this.overviewRequestId += 1;
            const requestId = this.overviewRequestId;
            this.loading = true;
            this.error = '';
            try {
                const {data} = await this.$axios.get('/eval-prompt/rulebook/overview');
                if (this.overviewRequestId !== requestId) return;
                this.overview = normalizeRulebookOverview(data);
            } catch (err) {
                if (this.overviewRequestId !== requestId) return;
                // 재조회 실패는 직전 성공 데이터를 지우지 않는다.
                const status = err?.response?.status;
                this.error = apiErrorMessage(
                    err,
                    status === 403
                        ? 'AI 판단 기준 관리 권한이 없습니다.'
                        : 'AI 판단 기준 상태를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.',
                );
            } finally {
                if (this.overviewRequestId === requestId) this.loading = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.eval-prompt-management {
    color: #2d3b4d;
    max-width: 1600px;
}

.page-header {
    align-items: flex-start;
    display: flex;
    justify-content: space-between;

    h4 {
        font-weight: 700;
    }
}

.page-kicker {
    color: #2867b2;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.08em;
}

.header-status {
    display: flex;
    flex-shrink: 0;
}

.stage-badge {
    border: 1px solid #d7dee6;
    font-size: 0.78rem;
    padding: 0.55rem 0.7rem;
}

.rulebook-shell {
    border-color: #dce3ea;
    box-shadow: 0 4px 16px rgba(31, 45, 61, 0.05);
}

::v-deep .rulebook-shell > .card-header {
    background: #fff;
    border-bottom: 1px solid #e2e7ec;
    padding: 0 1rem;
}

::v-deep .rulebook-shell > .card-header .nav-tabs {
    border: 0;
}

::v-deep .rulebook-shell > .card-header .nav-link {
    border: 0;
    border-bottom: 3px solid transparent;
    color: #667384;
    font-weight: 600;
    padding: 1rem 1.15rem 0.85rem;
}

::v-deep .rulebook-shell > .card-header .nav-link.active {
    border-bottom-color: #2867b2;
    color: #244f80;
}

::v-deep .rulebook-shell > .tab-content > .tab-pane {
    padding: 1.5rem;
}

@media (max-width: 767px) {
    .page-header {
        display: block;
    }

    .header-status {
        flex-wrap: wrap;
    }

    ::v-deep .rulebook-shell > .card-header .nav-link {
        font-size: 0.85rem;
        padding-left: 0.65rem;
        padding-right: 0.65rem;
    }

    ::v-deep .rulebook-shell > .tab-content > .tab-pane {
        padding: 1rem;
    }
}
</style>
